import * as R from 'ramda';
import * as usersReducer from '../../core/reducers/users';
import * as networksReducer from '../../core/reducers/networks';
import * as teamsReducer from '../../network/reducers/teams';
import * as reducerUtil from '../../../common/utils/reducer';
import { FileType } from '../../../common/utils/file';
import * as channelSelector from '../../organisation/selectors/channel';
import * as eventsReducer from '../../events/reducer';
import * as commentsReducer from './comments';
import * as attachmentsReducer from './attachments';
import * as likesReducer from './likes';
import * as pollsReducer from './polls';
import {
  SOCIAL_REACTION_TYPES,
  normalizeReactionType,
  normalizeReactionCounts,
} from '../constants/reactions';
import {
  ORGANISATION_RECEIVE_MESSAGES,
  ORGANISATION_RECEIVE_MESSAGE,
  ORGANISATION_CREATE_MESSAGE,
  ORGANISATION_UPDATE_MESSAGE,
} from '../../admin/actions';
import {
  SOCIAL_RECEIVE_FEED,
  SOCIAL_RECEIVE_SCHEDULED_MESSAGES,
  SOCIAL_RECEIVE_IMPORTANT_MESSAGES,
  SOCIAL_RECEIVE_UNREAD_MESSAGES,
  SOCIAL_POST_MESSAGE,
  SOCIAL_MESSAGE_HIGH_FIVE,
  SOCIAL_MESSAGE_UNHIGH_FIVE,
  SOCIAL_POST_COMMENT,
  SOCIAL_EDIT_MESSAGE,
  SOCIAL_TRANSLATE_MESSAGE,
  SOCIAL_UPDATE_MESSAGE,
  SOCIAL_DELETE_MESSAGE,
  SOCIAL_RECEIVE_MESSAGE,
  SOCIAL_VOTE_POLL,
  SOCIAL_DELETE_COMMENT,
  SOCIAL_TOGGLE_MESSAGE_IMPORTANT,
  SOCIAL_TOGGLE_MESSAGE_COMMENTS,
  SOCIAL_MARK_AS_READ,
} from '../actions';
import { EObjectTypes } from '../definitions';
import { EParentTypes } from '../../../common/definitions';

const isMessage = R.anyPass([
  R.propEq('object_type', EObjectTypes.MESSAGE),
  R.propEq('object_type', EObjectTypes.ORGANISATION_MESSAGE),
  R.propEq('object_type', EObjectTypes.RECOGNITION_MESSAGE)
]);

function sumReactionCounts(counts) {
  return SOCIAL_REACTION_TYPES.reduce((acc, t) => acc + (Number(counts[t]) || 0), 0);
}

const addMessageToStore = (acc, object) => R.assoc(object.source.id, {
  ...object.source,
  actions: object.actions,
  seen_count: object.seen_count,
  created_in: { type: object.parent_type, id: object.parent_id },
  publish_at: object.publish_at ?? object.source?.publish_at,
  processed_at: object.processed_at,
  liked_type: object.source.liked_type || (object.source.has_liked ? 'high_five' : null),
  likes_counts_details: normalizeReactionCounts(
    object.source.likes_counts_details,
    object.source.likes_count,
    object.source.liked_type || (object.source.has_liked ? 'high_five' : null)
  ),
}, acc);

export default (state = {}, action) => {
  switch (action.type) {
    case SOCIAL_RECEIVE_FEED:
    case SOCIAL_RECEIVE_SCHEDULED_MESSAGES:
    case SOCIAL_RECEIVE_IMPORTANT_MESSAGES:
    case SOCIAL_RECEIVE_UNREAD_MESSAGES:
    case ORGANISATION_RECEIVE_MESSAGES:
      return R.pipe(R.filter(isMessage), R.reduce(addMessageToStore, state))(action.messages || action.items);
    case SOCIAL_EDIT_MESSAGE:
    case SOCIAL_POST_MESSAGE:
    case SOCIAL_RECEIVE_MESSAGE:
    case ORGANISATION_RECEIVE_MESSAGE:
    case ORGANISATION_CREATE_MESSAGE:
    case ORGANISATION_UPDATE_MESSAGE:
    case SOCIAL_TOGGLE_MESSAGE_IMPORTANT:
    case SOCIAL_TOGGLE_MESSAGE_COMMENTS:
      if (!action.message) return state;

      return R.pipe(
        (currentState) => addMessageToStore(currentState, action.message),
        (currentState) => {
          if (action.type !== SOCIAL_EDIT_MESSAGE || action.publish_at === undefined) {
            return currentState;
          }

          const id = action.messageId
            || action.message.source?.id
            || action.message.source_id;

          if (!id || !currentState[id]) return currentState;

          return reducerUtil.update(id, (message) => ({
            ...message,
            publish_at: action.publish_at,
          }), currentState);
        },
      )(state);
    case SOCIAL_TRANSLATE_MESSAGE:
      return reducerUtil.update(action.messageId, (message) => R.merge(message, {
        translated_text: R.assoc(action.locale, action.translated_text, message.translated_text || {}),
      }), state);
    case SOCIAL_POST_COMMENT:
      return reducerUtil.update(action.messageId, (message) => R.merge(message, {
        comments_count: message.comments_count + 1,
      }), state);
    case SOCIAL_DELETE_COMMENT:
      return reducerUtil.update(action.messageId, R.clone, state);
    case SOCIAL_MESSAGE_HIGH_FIVE:
      return reducerUtil.update(action.messageId, (message) => R.merge(message, {
        has_liked: true,
        liked_type: normalizeReactionType(action.likeType),
        likes_count: message.has_liked ? message.likes_count : message.likes_count + 1,
        likes_counts_details: (() => {
          const likedFallback = message.has_liked ? (message.liked_type || 'high_five') : null;
          const currentCounts = normalizeReactionCounts(
            message.likes_counts_details,
            message.likes_count,
            likedFallback
          );
          const nextType = normalizeReactionType(action.likeType);
          const prevType = normalizeReactionType(action.previousLikeType || message.liked_type || 'high_five');

          if (message.has_liked && prevType !== nextType) {
            const nextCounts = { ...currentCounts };
            const sumBefore = sumReactionCounts(nextCounts);
            const fromPrev = nextCounts[prevType] || 0;
            if (fromPrev > 0) {
              nextCounts[prevType] = fromPrev - 1;
            } else {
              const sumExceptHighFive = SOCIAL_REACTION_TYPES
                .filter((t) => t !== 'high_five')
                .reduce((acc, t) => acc + (nextCounts[t] || 0), 0);
              const lumpOnlyHighFive = (nextCounts.high_five || 0) === message.likes_count
                && message.likes_count > 0
                && sumExceptHighFive === 0;
              if (lumpOnlyHighFive && prevType !== 'high_five') {
                nextCounts.high_five = Math.max((nextCounts.high_five || 0) - 1, 0);
              } else {
                const singleBucketType = SOCIAL_REACTION_TYPES.find(
                  (t) => (nextCounts[t] || 0) === message.likes_count && message.likes_count > 0
                );
                const singleBucketOnly = singleBucketType && SOCIAL_REACTION_TYPES.every(
                  (t) => t === singleBucketType || (nextCounts[t] || 0) === 0
                );
                if (singleBucketOnly && prevType !== nextType) {
                  nextCounts[singleBucketType] = Math.max((nextCounts[singleBucketType] || 0) - 1, 0);
                } else {
                  const fallbackType = SOCIAL_REACTION_TYPES.find(
                    (t) => t !== nextType && (nextCounts[t] || 0) > 0
                  );
                  if (fallbackType) {
                    nextCounts[fallbackType] = Math.max((nextCounts[fallbackType] || 0) - 1, 0);
                  }
                }
              }
            }
            nextCounts[nextType] = (nextCounts[nextType] || 0) + 1;
            const sumAfter = sumReactionCounts(nextCounts);
            if (sumAfter !== sumBefore) {
              nextCounts[nextType] = Math.max((nextCounts[nextType] || 0) - (sumAfter - sumBefore), 0);
            }
            return nextCounts;
          }

          if (message.has_liked) return currentCounts;

          return {
            ...currentCounts,
            [nextType]: (currentCounts[nextType] || 0) + 1,
          };
        })(),
      }), state);
    case SOCIAL_MESSAGE_UNHIGH_FIVE:
      return reducerUtil.update(action.messageId, (message) => R.merge(message, {
        has_liked: false,
        liked_type: null,
        likes_count: Math.max(message.likes_count - 1, 0),
        likes_counts_details: (() => {
          const likedFallback = message.has_liked ? (message.liked_type || 'high_five') : null;
          const currentCounts = normalizeReactionCounts(
            message.likes_counts_details,
            message.likes_count,
            likedFallback
          );
          const currentType = normalizeReactionType(action.likeType || message.liked_type || 'high_five');

          return {
            ...currentCounts,
            [currentType]: Math.max((currentCounts[currentType] || 0) - 1, 0),
          };
        })(),
      }), state);
    case SOCIAL_MARK_AS_READ:
      return reducerUtil.update(action.messageId, (message) => R.merge(message, {
        has_read: true,
      }), state);
    case SOCIAL_UPDATE_MESSAGE:
      return reducerUtil.update(action.messageId, (message) => R.merge(message, action.props), state);
    case SOCIAL_VOTE_POLL:
      // Re-render the message after voting on poll
      return {
        ...state,
        [action.messageId]: { ...state[action.messageId] },
      };
    case SOCIAL_DELETE_MESSAGE:
      return R.omit([action.messageId], state);
    default: return state;
  }
};

const getName = (object) => (object ? object.name : '"Verwijderd"');

const getParent = (state, { created_in: createdIn }) => R.cond([
  [R.equals(EParentTypes.NETWORK),
    () => networksReducer.findById(state, createdIn.id)],
  [R.equals(EParentTypes.TEAM),
    () => teamsReducer.findById(state, createdIn.id)],
  [R.equals(EParentTypes.ORGANISATION),
    () => state.organisation.selected],
  [R.equals(EParentTypes.CHANNEL),
    () => channelSelector.item(state, createdIn.id)],
  [R.equals(EParentTypes.EVENT),
    () => eventsReducer.findById(state, createdIn.id)],
  [R.T, R.always('')],
])(createdIn.type);

export const transformObject = (message, {
  comments = [],
  likes = [],
  attachments = [],
  seen = true,
  user,
  createdIn,
  poll,
  recognition,
}) => {
  const images = R.filter(R.propEq('file_type', FileType.IMAGE), attachments);
  const videos = R.filter(R.propEq('file_type', FileType.VIDEO), attachments);
  const files = R.filter(R.complement(R.either(R.propEq('file_type', FileType.IMAGE), R.propEq('file_type', FileType.VIDEO))), attachments);

  return {
    ...message,
    comments,
    likes,
    attachments: images,
    videos,
    files,
    seen,
    user,
    poll,
    created_in: createdIn,
    recognition,
  };
};

export const findById = R.curry((state, id) => {
  const message = state.social.messages[id];

  if (!message) return undefined;

  const comments = commentsReducer.findByMessage(state, id);
  const attachments = attachmentsReducer.findByMessage(state, id);
  const likes = likesReducer.findByMessage(state, id);
  const parent = getParent(state, message);

  return transformObject(message, {
    comments,
    likes,
    attachments,
    seen: !!state.social.seen.objects[message.object_id],
    user: usersReducer.findById(state, message.created_by),
    createdIn: {
      ...message.created_in,
      parent,
      values: getName(parent),
    },
    poll: pollsReducer.findByMessageId(state, message.id),
    ...(message.message_type === 'recognition_message' && message.recognition && {
      recognition: {
        ...message.recognition,
        receiver: usersReducer.findById(state, message.recognition.recipient_id)
      }
    })
  });
});
